/* eslint-disable no-underscore-dangle */
import AdobeAnalytics from './adobe';
import Amplitude from './amplitude';
import PostMessage from './post-message';
import RedditPixel from './redditpixel';
import Simon from './simon';
// Note: removed below while we wait for dxt changes to make it to prod
// import FloodlightPixel from './floodlightpixel';
// import GooglePixel from './googlepixel';

import { checkIfWidget, Logger } from '../utils';

function proxyDecoratorHandler(decoratorId, actionId, params) {
  try {
    let decoratorMap = window._amexResy_consumerWeb_proxyDecorators;
    if ((typeof decoratorMap !== 'object') || decoratorMap === null) {
      window._amexResy_consumerWeb_proxyDecorators = ({});
      decoratorMap = window._amexResy_consumerWeb_proxyDecorators;
    }

    let decoratorObj = decoratorMap[decoratorId];
    if (!decoratorObj) {
      decoratorMap[decoratorId] = ({
        queue: [],
      });
      decoratorObj = decoratorMap[decoratorId];
    }

    decoratorObj.queue.push({
      actionId,
      params,
    });

    if (decoratorObj.flushQueue) {
      decoratorObj.flushQueue();
    }
  } catch (e) {
    Logger.error(`[Analytics] ERROR (decoratorId: ${decoratorId}):`, e);
  }
}

/**
 * used for analytics services that are loaded
 * by the AMEX UCM platform
*/
const ProxyDecorator = (id) => ({
  event(name, props, callback) {
    proxyDecoratorHandler(id, 'event', [
      name,
      props,
      callback,
    ]);
  },
  page(location, referrer, metadata) {
    proxyDecoratorHandler(id, 'page', [
      location,
      referrer,
      metadata,
    ]);
  },

  marketingCustomPage(args) {
    proxyDecoratorHandler(id, 'gdaExplainerPage', [args]);
  },
  purchase(orderId, data) {
    proxyDecoratorHandler(id, 'purchase', [
      orderId,
      data,
    ]);
  },
  offersLastTouch(firstParams, lastParams) {
    proxyDecoratorHandler(id, 'offersLastTouch', [
      firstParams,
      lastParams,
    ]);
  },
  setUser(userData) {
    proxyDecoratorHandler(id, 'setUser', [
      userData,
    ]);
  },
  schedule(pixelId) {
    proxyDecoratorHandler(id, 'schedule', [
      pixelId,
    ]);
  },
  venuePixel(pixelId) {
    proxyDecoratorHandler(id, 'venuePixel', [
      pixelId,
    ]);
  },
  tddInit(...args) {
    proxyDecoratorHandler(id, 'tddInit', args);
  },
});

const Analytics = function (ResyAmexConfig = {}) {
  const DECORATORS = [
    AdobeAnalytics,
    Amplitude,
    // Note: removed below while we wait for dxt changes to make it to prod
    // FloodlightPixel,
    // GooglePixel,
    RedditPixel,
    Simon,
    PostMessage,
    ProxyDecorator('DoubleClick'),
    ProxyDecorator('FBPixel'),
    ProxyDecorator('Floodlight'),
    ProxyDecorator('TDD'),
  ];

  const event = (name, props, callback) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.event) value.event.call(this, name, props, callback);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const page = (location, referrer, metadata, userData) => {
    try {
      if (window.pageViewTimer) clearTimeout(window.pageViewTimer);
      window.pageViewTimer = setTimeout(() => {
        DECORATORS.forEach((value) => {
          if (value.page) value.page.call(this, location, referrer, metadata, userData, ResyAmexConfig);
        });
      }, 500);
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const tddInit = (...args) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.tddInit) {
          value.tddInit.call(this, ...args);
        }
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };
  const marketingCustomPage = (args) => {
    try {
      if (window.pageViewTimer) clearTimeout(window.pageViewTimer);
      window.pageViewTimer = setTimeout(() => {
        DECORATORS.forEach((value) => {
          if (value.marketingCustomPage) value.marketingCustomPage.call(this, args);
        });
      }, 500);
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const purchase = (orderId, data) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.purchase) value.purchase.call(this, orderId, data);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const offersLastTouch = (firstParams, lastParams) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.offersLastTouch) value.offersLastTouch.call(this, firstParams, lastParams);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const setUser = (userData) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.setUser) value.setUser.call(this, userData);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const setDetails = (booking) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.setDetails) value.setDetails.call(this, booking);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const setVenueName = (name) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.setVenueName) value.setVenueName.call(this, name);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const setVenueId = (id) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.setVenueId) value.setVenueId.call(this, id);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const schedule = (pixelId) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.schedule) value.schedule.call(this, pixelId);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const venuePixel = (pixelId) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.venuePixel) value.venuePixel.call(this, pixelId);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  const setVenueInventory = (snapshot) => {
    try {
      DECORATORS.forEach((value) => {
        if (value.setVenueInventory) value.setVenueInventory.call(this, snapshot);
      });
    } catch (err) {
      Logger.error('[Analytics] ERROR:', err);
    }
  };

  return {
    event,
    page,
    purchase,
    setUser,
    setDetails,
    setVenueName,
    setVenueId,
    schedule,
    venuePixel,
    offersLastTouch,
    marketingCustomPage,
    tddInit,
    setVenueInventory,
  };
};

if (!checkIfWidget()) Analytics.$inject = ['ResyAmexConfig'];
export default Analytics;
